import React, { useState, useEffect } from 'react';
import { postAPI, postAPIForm } from '../../../app/api';
import Swal from 'sweetalert2';

const SendEmail = () => {
    const [groups, setGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [ccEmail, setCcEmail] = useState('');
    const [replyToEmail, setReplyToEmail] = useState('');
    const [sending, setSending] = useState(false);
    const [attachmentFile, setAttachmentFile] = useState(null);
    const [attachmentUrl, setAttachmentUrl] = useState('');
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        fetchGroups();
    }, []);

    const fetchGroups = async () => {
        try {
            const response = await postAPI('communication-group/list', {});
            if (response?.status === 1) {
                setGroups(response?.data?.filter(g => g.status === 1) || []);
            }
        } catch (error) {
            console.error('Error fetching groups:', error);
        }
    };

    const handleSendEmail = async () => {
        if (!selectedGroup) {
            Swal.fire('Validation', 'Please select a group', 'warning');
            return;
        }

        if (!subject.trim()) {
            Swal.fire('Validation', 'Please enter email subject', 'warning');
            return;
        }

        if (!message.trim()) {
            Swal.fire('Validation', 'Please enter email message', 'warning');
            return;
        }

        const result = await Swal.fire({
            title: 'Confirm Send',
            text: 'Are you sure you want to send this email to all group members?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Send Email',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                setSending(true);
                const response = await postAPI('email/send', {
                    group_id: selectedGroup,
                    subject: subject,
                    message: message,
                    cc_email: ccEmail,
                    reply_to: replyToEmail,
                    sent_by: 'admin',
                    attachment_url: attachmentUrl
                });

                if (response?.status === 1) {
                    Swal.fire({
                        title: 'Success',
                        html: `Email sent successfully!<br/>
                               Total: ${response.data.total}<br/>
                               Sent: ${response.data.sent}<br/>
                               Failed: ${response.data.failed}<br/>
                               Attachments: ${response.data.attachments || 'No attachment'}`,
                        icon: 'success'
                    });
                    setSelectedGroup('');
                    setSubject('');
                    setMessage('');
                    setCcEmail('');
                    setReplyToEmail('');
                    setAttachmentFile(null);
                    setAttachmentUrl('');
                } else {
                    Swal.fire('Error', response?.msg || 'Failed to send email', 'error');
                }
            } catch (error) {
                console.error('Error sending email:', error);
                Swal.fire('Error', 'Failed to send email', 'error');
            } finally {
                setSending(false);
            }
        }
    };

    const handleFileUpload = async (file) => {
        if (!file) return '';

        console.log('🔄 Using CORRECT endpoint: file/uploadFile');
        
        const formData = new FormData();
        formData.append('original_file', file);
        formData.append('baseDir', 'email-attachments');

        try {
            setUploading(true);
            console.log('📤 Uploading to: file/uploadFile');
            const response = await postAPIForm('file/uploadFile', formData);

            if (response?.status === 1 && response?.filepath) {
                console.log('✅ Upload successful:', response.filepath);
                return response.filepath;
            } else {
                throw new Error('Upload failed');
            }
        } catch (error) {
            console.error('File upload error:', error);
            Swal.fire('Upload Error', 'Failed to upload attachment', 'error');
            return '';
        } finally {
            setUploading(false);
        }
    };

    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        if (file) {
            // Check file size (limit to 10MB)
            if (file.size > 10 * 1024 * 1024) {
                Swal.fire('File Too Large', 'Please select a file smaller than 10MB', 'warning');
                return;
            }
            
            setAttachmentFile(file);
            
            // Upload file immediately after selection
            const uploadedUrl = await handleFileUpload(file);
            if (uploadedUrl) {
                setAttachmentUrl(uploadedUrl);
                Swal.fire('Success', 'File uploaded successfully!', 'success');
            }
        }
    };

    const removeAttachment = () => {
        setAttachmentFile(null);
        setAttachmentUrl('');
    };

    return (
        <div className='card send-group-message'>
            <div className='card-body'>
                <div className='form-group mb-3'>
                    <label>Select Group *</label>
                    <select
                        className='form-control'
                        value={selectedGroup}
                        onChange={(e) => setSelectedGroup(e.target.value)}
                        disabled={sending}
                    >
                        <option value="">-- Select Group --</option>
                        <option value="universal">📢 Universal Group (All Members)</option>
                        {groups.map((group) => (
                            <option key={group.id} value={group.id}>
                                {group.group_name} ({group.member_count} members)
                            </option>
                        ))}
                    </select>
                </div>
                <div className='form-group mb-3'>
                    <label>Email Subject *</label>
                    <input
                        type='text'
                        placeholder='Enter email subject'
                        className='form-control'
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        disabled={sending}
                    />
                </div>
                <div className='form-group mb-3'>
                    <label>CC Email (Optional)</label>
                    <input
                        type='email'
                        placeholder='Enter CC email address (optional)'
                        className='form-control'
                        value={ccEmail}
                        onChange={(e) => setCcEmail(e.target.value)}
                        disabled={sending}
                    />
                    <small className="text-muted">Carbon copy - email will be sent to this address as well</small>
                </div>
                <div className='form-group mb-3'>
                    <label>Reply-To Email (Optional)</label>
                    <input
                        type='email'
                        placeholder='Enter reply-to email address (optional)'
                        className='form-control'
                        value={replyToEmail}
                        onChange={(e) => setReplyToEmail(e.target.value)}
                        disabled={sending}
                    />
                    <small className="text-muted">Recipients will reply to this email address</small>
                </div>
                <div className='form-group mb-3'>
                    <label>Email Message *</label>
                    <textarea
                        placeholder='Enter your email message here...'
                        className='form-control'
                        style={{ minHeight: '200px' }}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        disabled={sending}
                    ></textarea>
                </div>
                <div className='form-group mb-3'>
                    <label>Attachment (Optional)</label>
                    <div className="d-flex align-items-center gap-3">
                        <input
                            type="file"
                            className="form-control"
                            onChange={handleFileSelect}
                            disabled={sending || uploading}
                            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.bmp,.webp,.xlsx,.xls"
                        />
                        {uploading && (
                            <div className="text-info">
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Uploading...
                            </div>
                        )}
                    </div>
                    {attachmentFile && (
                        <div className="mt-2 p-2 bg-light border rounded d-flex align-items-center justify-content-between">
                            <small className="text-success">
                                📎 {attachmentFile.name} ({(attachmentFile.size / 1024 / 1024).toFixed(2)} MB)
                                {attachmentUrl && <span className="text-success ms-2">✓ Uploaded</span>}
                            </small>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={removeAttachment}
                                disabled={sending}
                            >
                                Remove
                            </button>
                        </div>
                    )}
                    <small className="text-muted">
                        Supported formats: PDF, DOC, DOCX, TXT, JPG, JPEG, PNG, GIF, BMP, WEBP, XLSX, XLS (Max: 10MB)
                    </small>
                </div>
                <div>
                    <button
                        className='btn btn-primary'
                        onClick={handleSendEmail}
                        disabled={sending}
                    >
                        {sending ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Sending...
                            </>
                        ) : (
                            'Send Email'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default SendEmail;